import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

interface GridOverlayProps {
  onAnimationStart?: () => void;
}

export function GridOverlay({ onAnimationStart }: GridOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const verticalLinesRef = useRef<HTMLDivElement[]>([]);
  const horizontalLinesRef = useRef<HTMLDivElement[]>([]);

  // Grid configuration
  const VERTICAL_LINES = 12;
  const HORIZONTAL_LINES = 8;

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate vertical lines (left to right stagger)
      gsap.fromTo(
        verticalLinesRef.current,
        { scaleY: 0, opacity: 0 },
        {
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: 0.03,
          ease: 'power2.out',
          onStart: onAnimationStart,
        }
      );

      // Animate horizontal lines (top to bottom stagger)
      gsap.fromTo(
        horizontalLinesRef.current,
        { scaleX: 0, opacity: 0 },
        {
          scaleX: 1,
          opacity: 1,
          duration: 0.5,
          stagger: 0.04,
          delay: 0.1,
          ease: 'power2.out',
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, [onAnimationStart]);

  // Exit animation (called from parent)
  const animateOut = () => {
    const tl = gsap.timeline();

    // Retract lines to center
    tl.to(verticalLinesRef.current, {
      scaleY: 0,
      duration: 0.4,
      stagger: {
        each: 0.02,
        from: 'edges',
      },
      ease: 'power2.in',
    });

    tl.to(
      horizontalLinesRef.current,
      {
        scaleX: 0,
        duration: 0.3,
        stagger: {
          each: 0.02,
          from: 'edges',
        },
        ease: 'power2.in',
      },
      '-=0.3'
    );

    return tl;
  };

  // Expose animateOut to parent via ref pattern
  useEffect(() => {
    if (containerRef.current) {
      (containerRef.current as HTMLDivElement & { animateOut: () => gsap.core.Timeline }).animateOut = animateOut;
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden pointer-events-none"
      aria-hidden="true"
    >
      {/* Vertical Lines */}
      {Array.from({ length: VERTICAL_LINES }).map((_, i) => (
        <div
          key={`v-${i}`}
          ref={(el) => {
            if (el) verticalLinesRef.current[i] = el;
          }}
          className="absolute top-0 bottom-0 w-px bg-white/[0.03] origin-top"
          style={{
            left: `${((i + 1) / (VERTICAL_LINES + 1)) * 100}%`,
            transform: 'scaleY(0)',
          }}
        />
      ))}

      {/* Horizontal Lines */}
      {Array.from({ length: HORIZONTAL_LINES }).map((_, i) => (
        <div
          key={`h-${i}`}
          ref={(el) => {
            if (el) horizontalLinesRef.current[i] = el;
          }}
          className="absolute left-0 right-0 h-px bg-white/[0.03] origin-left"
          style={{
            top: `${((i + 1) / (HORIZONTAL_LINES + 1)) * 100}%`,
            transform: 'scaleX(0)',
          }}
        />
      ))}

      {/* Intersection dots */}
      <div className="absolute inset-0">
        {Array.from({ length: 4 }).map((_, i) => {
          const positions = [
            { left: '25%', top: '25%' },
            { left: '75%', top: '25%' },
            { left: '25%', top: '75%' },
            { left: '75%', top: '75%' },
          ];
          return (
            <div
              key={`dot-${i}`}
              className="absolute w-1 h-1 rounded-full bg-white/5 -translate-x-1/2 -translate-y-1/2"
              style={positions[i]}
            />
          );
        })}
      </div>
    </div>
  );
}

export type GridOverlayRef = HTMLDivElement & { animateOut: () => gsap.core.Timeline };
